/**
 * GeoManager - Handles User Location and Proximity logic.
 */
class GeoManager {
    constructor() {
        this.watchId = null;
        this.currentPosition = null;
        this.listeners = [];
    }

    // Start tracking user location
    startTracking() {
        if (!navigator.geolocation) {
            console.error("Geolocation not supported");
            return;
        }

        if (this.watchId !== null) {
            // Already tracking, do not stack watches
            return;
        }

        const options = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        };

        this.watchId = navigator.geolocation.watchPosition(
            (pos) => this._handleSuccess(pos),
            (err) => this._handleError(err),
            options
        );
    }

    stopTracking() {
        if (this.watchId !== null) {
            navigator.geolocation.clearWatch(this.watchId);
            this.watchId = null;
        }
    }

    async promptLocationAccess(force = false) {
        if (!navigator.geolocation) return;

        // If permission is already granted, start tracking quietly
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const status = await navigator.permissions.query({ name: 'geolocation' });
                if (status.state === 'granted') {
                    this.startTracking();
                    return;
                }
            } catch (e) {
                // Ignore query error, continue to prompt
            }
        }

        // Check if user already dismissed it this session
        if (!force && sessionStorage.getItem('geo_prompt_dismissed') === 'true') {
            return;
        }

        try {
            const { showLocationPrompt } = await import('../components/LocationPrompt.js');
            showLocationPrompt({ force });
        } catch (e) {
            console.warn("Failed to load location prompt:", e);
        }
    }

    // Add listener for location updates
    onLocationUpdate(callback) {
        this.listeners.push(callback);
    }

    _handleSuccess(position) {
        const { latitude, longitude, accuracy } = position.coords;
        this.currentPosition = { lat: latitude, lng: longitude, accuracy: accuracy };

        // Notify listeners
        this.listeners.forEach(cb => cb(this.currentPosition));
    }

    // Force a fresh read of the location
    forceRefreshPosition() {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) return reject("No geo");
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    this._handleSuccess(pos);
                    resolve(pos);
                },
                (err) => {
                    this._handleError(err);
                    reject(err);
                },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );
        });
    }

    _handleError(error) {
        console.warn("Geo Error:", error.message);
    }

    // Haversine formula for distance in meters
    calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 6371e3; // metres
        const φ1 = lat1 * Math.PI / 180; // φ, λ in radians
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // in metres
    }

    /**
     * Verify if user is within range of POI.
     * Includes Anti-Cheat checks for Mock Locations.
     */
    checkIn(userLat, userLng, userAccuracy, poiLat, poiLng) {
        // 1. Accuracy Check (Anti-Cheat)
        // If accuracy is too perfect (0) or too bad (>100m for unlocking), reject.
        // Mock locations often have 0 accuracy or very specific fixed values.
        if (userAccuracy === 0) {
            return { success: false, message: "Segnale GPS simulato rilevato (Accuratezza 0)." };
        }
        if (userAccuracy > 200) {
            return { success: false, message: "Segnale GPS troppo debole. Avvicinati o attendi una ricezione migliore." };
        }

        // 2. Distance Check
        const district = this.calculateDistance(userLat, userLng, poiLat, poiLng);
        const UNLOCK_RADIUS = 50; // meters

        if (district <= UNLOCK_RADIUS) {
            return { success: true, message: "Sei arrivato!", distance: district };
        } else {
            return { success: false, message: "Sei ancora troppo lontano.", distance: district };
        }
    }

    /**
     * Text-to-Speech distance announcement
     */
    announceDistance(meters) {
        if (!('speechSynthesis' in window)) return;

        const speech = new SpeechSynthesisUtterance();
        speech.lang = 'it-IT';

        if (meters < 50) {
            speech.text = "Sei arrivato a destinazione. Il quiz è ora disponibile.";
        } else {
            speech.text = `Mancano circa ${Math.round(meters)} metri.`;
        }
        window.speechSynthesis.speak(speech);
    }
}

export const geoManager = new GeoManager();
